// src/pages/dashboard/TablePlanner.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useNotification } from "../../../utils/notification_context";
// Components
import GuestListPanel from "../../../components/dashboard/table-planner/GuestListPanel";
import TablesPanel from "../../../components/dashboard/table-planner/TablesPanel";
import LoadingModal from "../../../components/loading/LoadingModal";
import OverviewPanel from "../../../components/dashboard/table-planner/OverviewPanel";
// Utils
import {
  fetchAcceptedRSVPList,
  saveTablePlan,
  fetchTablePlan,
} from "../../../utils/firebase_util";
import {
  exportTablePlanningToExcel,
  exportTablePlanningToExcelOld,
} from "../../../utils/export_util";

export default function TablePlanner() {
  const { showNotification, showErrorNotification } = useNotification();

  const [allAcceptedGuests, setAllAcceptedGuests] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [draggedGuest, setDraggedGuest] = useState(null);
  const [invitedByFilter, setInvitedByFilter] = useState("all");
  const [tableSearchTerm, setTableSearchTerm] = useState("");
  const [selectedGuest, setSelectedGuest] = useState(null);

  const scrollIntervalRef = useRef(null);
  const tableRefs = useRef({});
  const prevTablesCount = useRef(tables.length);
  const isInitialLoad = useRef(true);

  const getItemTotalPax = (item) => {
    if (!item) return 0;
    let totalPax = 1;
    if (item.partnerAttending) totalPax += 1;
    if (item.childrenAttending && item.childrenCount)
      totalPax += Number(item.childrenCount);
    return totalPax;
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [acceptedGuests, rawPlan] = await Promise.all([
          fetchAcceptedRSVPList(),
          fetchTablePlan(),
        ]);

        // Step 2: Process the master guest list and create an efficient lookup map.
        const guestsWithPax = acceptedGuests.map((g) => ({
          ...g,
          pax: getItemTotalPax(g),
          babyChairCount: g.babyChairCount,
        }));
        const allGuestsMap = new Map(guestsWithPax.map((g) => [g.id, g]));

        // Set the master list for the 'Unseated Guests' panel.
        setAllAcceptedGuests(guestsWithPax);

        // Step 3: If a plan was loaded, use the map to populate it with full guest objects.
        let initialTables = [];
        if (rawPlan) {
          initialTables = rawPlan.map((table) => ({
            ...table,
            guests: table.guests
              .map((guestId) => allGuestsMap.get(guestId)) // The lookup happens here!
              .filter(Boolean), // Clean up any guests who might have been deleted
          }));
        }

        setTables(initialTables);
      } catch (error) {
        console.error("Error loading table planner data:", error);
        showErrorNotification("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (isInitialLoad.current) {
      // If tables have been loaded, set the flag to false for all future renders.
      // This ensures we don't scroll on the first load but enable it for later actions.
      if (tables.length > 0) {
        isInitialLoad.current = false;
      }
    } else {
      // Check if a table was added by comparing the current and previous counts
      if (tables.length > prevTablesCount.current) {
        const newTable = tables[tables.length - 1]; // Assumes the new table is at the end

        // Use a timeout to ensure the new table has rendered and its ref is available
        setTimeout(() => {
          scrollToTable(newTable.id);
        }, 100); // 100ms delay is generally sufficient
      }
    }

    // Update the ref with the current table count for the next check
    prevTablesCount.current = tables.length;
  }, [tables]);

  const unseatedGuests = useMemo(() => {
    const seatedGuestIds = new Set(
      tables.flatMap((table) => table.guests.map((g) => g.id))
    );
    return allAcceptedGuests.filter((guest) => !seatedGuestIds.has(guest.id));
  }, [allAcceptedGuests, tables]);

  const filteredTables = useMemo(() => {
    if (!tableSearchTerm) {
      return tables; // Return all tables if search is empty
    }
    return tables.filter(
      (table) =>
        table.name.toLowerCase().includes(tableSearchTerm.toLowerCase()) ||
        String(table.tableNumber).includes(tableSearchTerm)
    );
  }, [tables, tableSearchTerm]);

  const stopScrolling = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  const handleDragOverWithScrolling = (e) => {
    e.preventDefault();
    const SCROLL_ZONE_SIZE = 80; // Pixels from edge to trigger scroll
    const SCROLL_SPEED = 15; // Pixels to scroll per interval

    if (e.clientY < SCROLL_ZONE_SIZE) {
      // Top scroll zone
      if (!scrollIntervalRef.current) {
        scrollIntervalRef.current = setInterval(() => {
          window.scrollBy(0, -SCROLL_SPEED);
        }, 15);
      }
    } else if (e.clientY > window.innerHeight - SCROLL_ZONE_SIZE) {
      // Bottom scroll zone
      if (!scrollIntervalRef.current) {
        scrollIntervalRef.current = setInterval(() => {
          window.scrollBy(0, SCROLL_SPEED);
        }, 15);
      }
    } else {
      // Out of scroll zone
      stopScrolling();
    }
  };

  // Handler Functions
  const handleGuestSelect = (guest) => {
    if (selectedGuest?.id === guest.id) {
      setSelectedGuest(null); // Deselect if the same guest is tapped again
    } else {
      setSelectedGuest(guest);
    }
  };

  const handleAssignGuest = (targetTableId) => {
    if (!selectedGuest) return; // Should not happen, but a good safeguard

    const targetTable = tables.find((t) => t.id === targetTableId);
    if (!targetTable) return;

    const currentPax = targetTable.guests.reduce((sum, g) => sum + g.pax, 0);
    if (currentPax + selectedGuest.pax > targetTable.capacity) {
      showErrorNotification("Table is full. Cannot add guest.", true);
      return; // Do not deselect, let user try another table
    }

    setTables((prevTables) =>
      prevTables.map((table) =>
        table.id === targetTableId
          ? { ...table, guests: [...table.guests, selectedGuest] }
          : table
      )
    );
    setSelectedGuest(null); // Deselect after successful assignment
  };

  const handleDragStart = (e, guest) => {
    e.dataTransfer.setData("type", "guest"); // Set the drag type
    e.dataTransfer.setData("guest", JSON.stringify(guest));
    setDraggedGuest(guest); // Keep for legacy/mobile logic if needed
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = (e, targetTableId) => {
    stopScrolling();
    e.preventDefault();
    const type = e.dataTransfer.getData("type");
    if (type !== "guest") return; // Only handle guest drops here

    const guest = JSON.parse(e.dataTransfer.getData("guest"));
    if (!guest) return;

    const targetTable = tables.find((t) => t.id === targetTableId);
    if (!targetTable) return;
    const currentPax = targetTable.guests.reduce((sum, g) => sum + g.pax, 0);
    if (currentPax + guest.pax > targetTable.capacity) {
      showErrorNotification("Table is full. Cannot add guest.", true);
      return;
    }
    setTables((prevTables) =>
      prevTables.map((table) =>
        table.id === targetTableId
          ? { ...table, guests: [...table.guests, guest] }
          : table
      )
    );
    setDraggedGuest(null);
  };

  const handleRearrangeTables = (draggedTableId, targetTableId) => {
    // Find the original indices of the dragged and target tables
    const draggedIndex = tables.findIndex((t) => t.id === draggedTableId);
    const targetIndex = tables.findIndex((t) => t.id === targetTableId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // 1. Create a mutable copy of the tables array
    const newTables = [...tables];

    // 2. Remove the dragged table from its original position
    const [draggedTable] = newTables.splice(draggedIndex, 1);

    // 3. Insert the dragged table at the target's position
    newTables.splice(targetIndex, 0, draggedTable);

    // 4. Re-number all tables sequentially and update state
    const renumberedTables = newTables.map((table, index) => ({
      ...table,
      tableNumber: index + 1,
    }));

    setTables(renumberedTables);
  };

  const handleAddTable = () => {
    const defaultName = `Table ${tables.length + 1}`;
    const tableName = prompt("Enter table name:", defaultName);
    if (!tableName) return;

    const capacity = parseInt(prompt("Enter table capacity:", "10"), 10);
    if (isNaN(capacity) || capacity <= 0) return;

    // Calculate the next available table number
    const nextTableNumber =
      tables.length > 0 ? Math.max(...tables.map((t) => t.tableNumber)) + 1 : 1;

    const newTable = {
      id: `table-${Date.now()}`,
      tableNumber: nextTableNumber, // Assign the sequential number
      name: tableName,
      capacity: capacity,
      guests: [],
    };
    setTables((prev) => [...prev, newTable]);
  };

  const handleRemoveGuest = (guestToRemove, fromTableId) => {
    setTables((prevTables) =>
      prevTables.map((table) =>
        table.id === fromTableId
          ? {
              ...table,
              guests: table.guests.filter((g) => g.id !== guestToRemove.id),
            }
          : table
      )
    );
  };
  const handleUpdateTableName = (tableId, newName) => {
    setTables((prev) =>
      prev.map((t) => (t.id === tableId ? { ...t, name: newName } : t))
    );
  };
  const handleUpdateTableCapacity = (tableId, newCapacity) => {
    const table = tables.find((t) => t.id === tableId);
    const currentPax = table.guests.reduce((sum, g) => sum + g.pax, 0);
    if (newCapacity < currentPax) {
      showErrorNotification(
        `Capacity cannot be less than the number of seated guests (${currentPax}).`
      );
      return;
    }
    setTables((prev) =>
      prev.map((t) => (t.id === tableId ? { ...t, capacity: newCapacity } : t))
    );
  };
  const handleDeleteTable = (tableId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this table? All guests will be unseated."
      )
    ) {
      setTables((prev) => prev.filter((t) => t.id !== tableId));
    }
  };
  const handleSaveChanges = async () => {
    setIsSaving(true);
    setLoading(true);
    try {
      await saveTablePlan(tables);
      showNotification("Seating plan saved successfully!");
    } catch (error) {
      console.error("Failed to save seating plan:", error);
      showErrorNotification("Could not save changes.");
    } finally {
      setIsSaving(false);
      setLoading(false);
    }
  };

  const filteredUnseatedGuests = useMemo(() => {
    return unseatedGuests
      .filter((guest) =>
        guest.guestName.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .filter((guest) => {
        if (invitedByFilter === "all") return true;
        return guest.invitedBy === invitedByFilter;
      });
  }, [unseatedGuests, searchTerm, invitedByFilter]);

  const handleExport = () => {
    try {
      exportTablePlanningToExcel(tables);
      exportTablePlanningToExcelOld(tables, unseatedGuests);
      showNotification("Data exported successfully!");
    } catch (error) {
      console.error("Export failed:", error);
      showErrorNotification("Could not export data.");
    }
  };

  const scrollToTable = (tableId) => {
    const tableElement = tableRefs.current[tableId];
    if (tableElement) {
      tableElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  return (
    <div className="bg-[#f8ecdc] min-h-screen font-['Poppins'] text-gray-700 p-4 md:p-8">
      <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <Link
          to="/dashboard"
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 flex items-center space-x-2 shadow-sm"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          <span>Dashboard</span>
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-[#931b1c]">Table Planner</h1>
          <p className="text-gray-500">
            Drag and drop on desktop, Tap on mobile. Tap guests to arrange
            seating.
          </p>
        </div>
        <div className="flex items-center gap-4 mt-4 md:mt-0">
          <button
            onClick={handleExport}
            className="flex items-center justify-center space-x-2 px-6 py-2 font-semibold border-[3px] border-[#931b1c] text-[#931b1c] rounded-lg  hover:border-[#cc6262] hover:text-[#cc6262]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
            <span>Export</span>
          </button>
          <button
            onClick={handleSaveChanges}
            disabled={isSaving}
            className="mt-4 md:mt-0 px-6 py-2 bg-[#931b1c] border-[3px] border-[#931b1c] text-white font-semibold rounded-lg hover:bg-opacity-90 disabled:bg-opacity-50"
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-4 gap-8">
        <GuestListPanel
          guests={filteredUnseatedGuests}
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          onDragStart={handleDragStart}
          onDragEnd={stopScrolling}
          invitedByFilter={invitedByFilter}
          onFilterChange={setInvitedByFilter}
          selectedGuest={selectedGuest}
          onGuestSelect={handleGuestSelect}
        />
        <TablesPanel
          tables={filteredTables}
          onAddTable={handleAddTable}
          onDrop={handleDrop}
          onUpdateTableName={handleUpdateTableName}
          onUpdateTableCapacity={handleUpdateTableCapacity}
          onDeleteTable={handleDeleteTable}
          onRemoveGuest={handleRemoveGuest}
          onMainDragOver={handleDragOverWithScrolling}
          tableSearchTerm={tableSearchTerm}
          onTableSearchChange={(e) => setTableSearchTerm(e.target.value)}
          selectedGuest={selectedGuest}
          onAssignGuest={handleAssignGuest}
          onRearrangeTables={handleRearrangeTables} // Pass the new handler
          tableRefs={tableRefs}
        />

        <div className="hidden 2xl:block">
          <OverviewPanel
            tables={tables}
            onRearrangeTables={handleRearrangeTables}
            onTableSelect={scrollToTable}
          />
        </div>
      </div>

      <LoadingModal isLoading={loading || isSaving} />
    </div>
  );
}
